import React, { useState, useEffect, useRef } from 'react';
import { sound, isMuted } from '../utils/sound';

interface VideoPopupProps {
  videoUrl: string;
  onClose: () => void;
}

const VideoPopup: React.FC<VideoPopupProps> = ({ videoUrl, onClose }) => {
  const [isClosing, setIsClosing] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Set initial muted state and start playback after 0.5 sec
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted();

      // Start playback after 0.5 seconds
      const timer = setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(err => {
            // Handle autoplay error (some browsers require user interaction)
            console.log('Autoplay prevented:', err);
          });
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, []);

  // Handle Escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isClosing) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isClosing]);

  const handleClose = () => {
    if (isClosing) return;

    sound.clickClose();

    // Pause video before closing
    if (videoRef.current) {
      videoRef.current.pause();
    }

    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 200); // Match animation duration
  };

  return (
    <div className={`video-popup-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`video-popup-content ${isClosing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <button className="video-close-button" onClick={handleClose}>
          ✕
        </button>

        <div className="video-container">
          <video
            ref={videoRef}
            src={videoUrl}
            className="video-player"
            controls
            disablePictureInPicture
            controlsList="nodownload noplaybackrate"
          />
        </div>
      </div>
    </div>
  );
};

export default VideoPopup;
